/**
 * API client — all calls to the backend API Gateway.
 *
 * Security (security.md §1):
 *   - Tokens stored in memory only (not localStorage/sessionStorage)
 *   - Token passed via Authorization header
 *   - PKCE flow handled by Cognito hosted UI
 */

const API_BASE = import.meta.env.VITE_API_URL ?? '';

let accessToken: string | null = null;

export function setAccessToken(token: string | null) {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

export async function apiCall<T = unknown>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options.headers as Record<string, string>),
  };

  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers,
  });

  if (response.status === 401) {
    // Token expired — trigger re-auth
    setAccessToken(null);
    window.location.href = '/login';
    throw new Error('Unauthorized');
  }

  if (!response.ok) {
    const error = await response.json().catch(() => ({ message: 'Request failed' }));
    throw new ApiError(error.message ?? `HTTP ${response.status}`, response.status);
  }

  return response.json() as Promise<T>;
}

/** Error with HTTP status code preserved for downstream handling */
export class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

// Convenience methods
export const api = {
  get: <T = unknown>(path: string) => apiCall<T>(path, { method: 'GET' }),
  post: <T = unknown>(path: string, body: unknown) =>
    apiCall<T>(path, { method: 'POST', body: JSON.stringify(body) }),
  put: <T = unknown>(path: string, body: unknown) =>
    apiCall<T>(path, { method: 'PUT', body: JSON.stringify(body) }),
  delete: <T = unknown>(path: string) => apiCall<T>(path, { method: 'DELETE' }),
};
